/* eslint-disable max-lines */
import { useEffect, useState, useCallback, useRef } from 'react'
import { Box, VStack, HStack, useDisclosure, Button, Text } from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import ContextBanner from 'components/ContextBanner'
import get from 'lodash/get'
import isNumber from 'lodash/isNumber'
import { observer } from 'mobx-react'
import qs from 'qs'
import Highlighter from 'react-highlight-words'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { archiveCollectionById, shareCollectionsToGroups } from 'API/collection'
import { archiveProcessById } from 'API/process'
import { addSearchHistory } from 'API/search'
import { IPagination } from 'components/CkTable'
import CkPagination from 'components/CkPagination'
import DarkTable from 'components/DarkTable'
import { DarkRowKey } from 'components/DarkTable/types'
import { DARK_GREY, DARK_TEXT_MUTED, DARK_TEXT_PRIMARY } from 'constants/dark-tokens'
import { buildSearchResultColumns } from './components/search-result-columns'
import { IItemData } from './components/SearchResultsList'
import ControlledShareWithGroupsModal from 'components/ControlledShareWithGroupsModal'
import GlobalSpinner from 'components/GlobalSpinner'
import ModalConfirm from 'components/ModalConfirm'
import ShareWithGroupsDialog from 'components/ShareWithGroupsDialog'
import SvgIcon from 'components/SvgIcon'
import SearchPageHeader from 'components/Topbar/components/SearchPageHeader'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { ICollection } from 'interfaces/collection'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IProcessWithRelations } from 'interfaces/process'
import { IStepWithRelations } from 'interfaces/step'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import routes from 'routes'
import CollectionIcon from '../../../assets/icons/collection-icon.svg?react'
import { IProcess } from '../../../interfaces/process'
import { getValidArray } from '../../../utils/common'
import CollectionOverview from '../CollectionsPage/CollectionOverview'
import ProcessSummary from '../Common/ProcessSummary'
import EmptyResult from './components/EmptyResult'
import UserFeedback from './components/UserFeedback'
import {
  findFullWordChunks,
  updateViewOnlyItemsAfterSelectAll,
  isUserHavePermissionForCollection,
  addAllStepsOfProcessToCommonLibrary
} from './utils'
import styles from './searchPage.module.scss'
import CopyProcessModal from '../ProcessDetailPage/components/CopyProcessModal'
import { isBasicUserByMembership } from 'utils/auth/role'
import { getStackCapabilities } from 'utils/stack/stack-capabilities'

export interface ISearchPageQuery {
  searchText?: string
  page?: number
}

// Manage-mode bulk-action buttons shown in the DarkTable actions-column header —
// mirrors the ArchivePage selection bar so the two dark pages read identically.
const selectionBarSx = {
  h: '32px',
  px: '14px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 500,
  bg: 'transparent',
  color: DARK_TEXT_PRIMARY,
  border: `1px solid ${DARK_GREY}`,
  _hover: { bg: 'rgba(255,255,255,0.06)' },
  _disabled: { opacity: 0.4, cursor: 'not-allowed' }
}

const SearchPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const query: ISearchPageQuery = qs.parse(location.search, { ignoreQueryPrefix: true })

  const searchText: string = query?.searchText ?? ''
  const page: number = query?.page ?? 1
  const { isOpen: isOpenProcess, onOpen: onOpenProcess, onClose: onCloseProcess } = useDisclosure()
  const { isOpen: isOpenCollection, onOpen: onOpenCollection, onClose: onCloseCollection } = useDisclosure()
  const {
    isOpen: isOpenConfirmAddToCommonLibrary,
    onOpen: onOpenConfirmAddToCommonLibrary,
    onClose: onCloseConfirmAddToCommonLibrary
  } = useDisclosure()
  const { searchStore, processStore, collectionStore, authStore, groupStore } = useStores()
  const { selectedProcess } = processStore
  const { userDetail } = authStore
  // Page-level single-team filter (coach+), mirroring the other list pages. Backed by
  // the global active org, so switching re-scopes the search below.
  // Org scope from the active organization; the banner owns the switch.
  const activeOrgId = authStore.activeOrgId
  // Skip the mount run so we don't double-fetch alongside the searchText/page effects.
  const isFirstTeamRender = useRef(true)

  // Add null checks to prevent React error #300
  if (!userDetail) {
    return <GlobalSpinner />
  }

  const currentUserId = userDetail.id
  const [selectedCollectionId, setSelectedCollectionId] = useState(0)
  const [selectedProcessIdList, setSelectedProcessIdList] = useState<number[]>([])
  const [selectedCollectionIdList, setSelectedCollectionIdList] = useState<number[]>([])
  const [isOpenShareCollectionToGroupDialog, setOpenShareCollectionToGroupDialog] = useState(false)
  const [isOpenBulkShareModal, setOpenBulkShareModal] = useState(false)
  const { totalResults, searchResults, size, isSearching, isManageMode, searchFilter } = searchStore
  const [isOpenArchiveModal, setIsOpenArchiveModal] = useState(false)
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
  const [selectedViewOnlyProcessIds, setSelectedViewOnlyProcessIds] = useState<number[]>([])
  const [selectedViewOnlyCollectionIds, setSelectedViewOnlyCollectionIds] = useState<number[]>([])
  const totalSelectedItems = selectedCollectionIdList.length + selectedProcessIdList.length
  const isBasicUser = isBasicUserByMembership(authStore.currentMembership)
  const isSelectedViewOnlyItems = !!(selectedViewOnlyProcessIds?.length || selectedViewOnlyCollectionIds?.length)
  const {
    isOpen: isOpenCopyProcessModal,
    onOpen: openCopyProcessModal,
    onClose: closeCopyProcessModal
  } = useDisclosure()

  async function handleSelectItem(item: IEsGlobalSearchItem) {
    const id = isNumber(+(item?.correlatingId ?? '')) ? +(item?.correlatingId ?? '') : 0
    if (!id) return
    if (item?.collectionName === EEsGlobalSearchCollection.PROCESS) {
      let newArray: number[] = [...selectedProcessIdList]
      if (newArray.indexOf(id) !== -1) {
        newArray.splice(newArray.indexOf(id), 1)
        setSelectedProcessIdList(newArray)
        setSelectedViewOnlyProcessIds(selectedViewOnlyProcessIds.filter(processId => processId !== id))
      } else {
        // No per-user permission lookup for a Stack: `stackCapabilitiesOf` decides
        // per row what this viewer may do to it, and a Player may do nothing.
        setSelectedProcessIdList([...newArray, id])
      }
      processStore.setSelectedProcessId(id)
    }

    if (item?.collectionName === EEsGlobalSearchCollection.COLLECTION) {
      let newArray: number[] = [...selectedCollectionIdList]
      if (newArray.indexOf(id) !== -1) {
        newArray.splice(newArray.indexOf(id), 1)
        setSelectedCollectionIdList(newArray)
        setSelectedViewOnlyCollectionIds(selectedViewOnlyCollectionIds.filter(collectionId => collectionId !== id))
      } else {
        setSelectedCollectionIdList([...newArray, id])
        if (isBasicUser) {
          const canEdit = await isUserHavePermissionForCollection(currentUserId, isBasicUser, id)
          if (!canEdit && !selectedViewOnlyCollectionIds.includes(id)) {
            setSelectedViewOnlyCollectionIds([...selectedViewOnlyCollectionIds, id])
          }
        }
      }
    }
  }

  async function handleArchiveItems() {
    try {
      if (selectedProcessIdList?.length) {
        await Promise.all([...getValidArray(selectedProcessIdList)].map(id => archiveProcessById(id)))
      }
      if (selectedCollectionIdList?.length) {
        await Promise.all([...getValidArray(selectedCollectionIdList)].map(id => archiveCollectionById(id)))
      }
      searchStore.search()
      setIsOpenArchiveModal(false)
      setSelectedProcessIdList([])
      setSelectedCollectionIdList([])
      toast.success('Archived items successfully')
    } catch (error: any) {
      toast.error('Archived items failed')
    }
  }

  function selectAndOpenShareModal(item: IEsGlobalSearchItem) {
    if (item?.collectionName === EEsGlobalSearchCollection.COLLECTION) {
      collectionStore.getCollectDetail(item?.correlatingId ?? 0)
      setOpenShareCollectionToGroupDialog(true)
    }
  }

  // DarkTable emits the full new selection (single toggle OR select-all) as composite
  // `${collectionName}::${id}` keys — split them back into the per-type id lists (process
  // and collection ids can collide numerically, so the type prefix is required) and
  // recompute the view-only set for basic users.
  function handleSelectionChange(keys: DarkRowKey[]) {
    const newProcessIdList: number[] = []
    const newCollectionIdList: number[] = []

    keys.forEach(key => {
      const [type, idString] = String(key).split('::')
      const id = Number(idString)
      if (!Number.isFinite(id) || !id) return
      if (type === EEsGlobalSearchCollection.PROCESS) {
        newProcessIdList.push(id)
      } else {
        newCollectionIdList.push(id)
      }
    })

    setSelectedProcessIdList(newProcessIdList)
    setSelectedCollectionIdList(newCollectionIdList)

    const selectedItems = getValidArray(searchResults).filter(item => {
      const id = +(item?.correlatingId ?? '')
      return item?.collectionName === EEsGlobalSearchCollection.PROCESS
        ? newProcessIdList.includes(id)
        : newCollectionIdList.includes(id)
    })
    updateViewOnlyItemsAfterSelectAll(
      currentUserId,
      selectedItems,
      setSelectedViewOnlyProcessIds,
      isBasicUser,
      setSelectedViewOnlyCollectionIds
    )
  }

  const clearSelectedItems = useCallback(() => {
    setSelectedProcessIdList([])
    setSelectedCollectionIdList([])
    setSelectedViewOnlyProcessIds([])
  }, [])

  function gotoPage(newPage: number): void {
    navigate(`${routes.search.value}?searchText=${searchText}&page=${newPage}`)
  }

  const pagination: IPagination = {
    gotoPage,
    pageIndex: Number(page),
    tableLength: totalResults
  }

  function handleOpenProcessModal(item: IProcessWithRelations): void {
    processStore.setSelectedProcess(item)
    processStore.setSelectedProcessId(item?.id ?? 0)
    onOpenProcess()
  }

  function handleOpenCollectionModal(item: ICollection): void {
    setSelectedCollectionId(item?.id ?? 0)
    onOpenCollection()
  }

  function handleChangePageSize(newPageSize: number): void {
    searchStore.changeSize(newPageSize)
    searchStore.search()
    gotoPage(1)
  }

  const setPageSize = (newPageSize: number): void => {
    handleChangePageSize(newPageSize)
  }

  // Collections only — a Stack is not shared any more, so a selection that holds one
  // cannot be shared in bulk either (`canBulkShare` keeps the button out of reach).
  async function onSubmitBulkShare() {
    try {
      if (selectedCollectionIdList?.length) {
        await shareCollectionsToGroups(selectedGroupIds, selectedCollectionIdList)
      }
      searchStore.search()
      setSelectedGroupIds([])
      setSelectedCollectionIdList([])
      setOpenBulkShareModal(false)
      toast.success('Share items successfully')
    } catch (error: any) {
      toast.error('Share items failed')
    }
  }

  const handleItemClick = (isProcess: boolean, item: IEsGlobalSearchItem, index: number) => {
    if (isProcess) {
      searchText &&
        addSearchHistory({
          keyword: searchText,
          totalResult: totalResults,
          clickedIndex: (page - 1) * size + index + 1,
          userId: currentUserId,
          processId: item?.correlatingId ?? 0
        })
      navigate(routes.processes.processId.value(String(item?.correlatingId ?? '')))
    } else {
      searchText &&
        addSearchHistory({
          keyword: searchText,
          totalResult: totalResults,
          clickedIndex: (page - 1) * size + index + 1,
          userId: currentUserId,
          processId: item?.correlatingId ?? 0
        })
      navigate(routes.collections.collectionId.value(String(item?.correlatingId ?? '')))
    }
  }

  /**
   * Capabilities for one row's Stack, from the same helper the detail page uses.
   * The row carries `ownerMembershipId` and its `collaborators`, which is all the
   * decision needs.
   */
  const stackCapabilitiesOf = (stack: unknown) =>
    getStackCapabilities(
      {
        roleId: authStore.maxRoleAcrossTeams,
        membershipId: authStore.currentMembershipId
      },
      stack as IProcessWithRelations
    )

  const dataInTable: IItemData[] = getValidArray<IEsGlobalSearchItem>(searchResults).map((item, index) => {
    const isProcess: boolean = item?.collectionName === EEsGlobalSearchCollection.PROCESS
    const title: string = item.correlatingData?.name ?? ''
    const subSearchSource = item?.highlight?.subSearchSource?.[0] ?? ''
    const searchSource = (item?.highlight?.searchSource?.[0] ?? '') + subSearchSource
    const nameHightlight: string = item?.highlight?.name?.[0] ?? ''
    const searchSourceWithoutEm: string = searchSource.replace(/<[^>]*>?/gm, '')
    const nameString = nameHightlight.split('<em>').map((i: string) => i.split('</em>')[0])
    const searchString: string[] = searchSource.split('<em>').map((i: string) => i.split('</em>')[0])
    const searchWords: string[] = searchString.length > 1 ? searchString.slice(1) : []
    searchWords.unshift(searchText)
    const nameWords: string[] = nameString.length > 1 ? nameString.slice(1) : []

    const id = +(item?.correlatingId ?? '')
    const steps: IStepWithRelations[] = get(item, 'correlatingData.steps', [])
    const editorGroupIds = getValidArray(groupStore.groupMembers)
      .filter(groupMember => (groupMember?.permissionId ?? 0) < IGroupMemberPermissionId.VIEWER)
      .map(groupMember => groupMember?.groupId)
    const processCollectionGroupIds: number[] = []

    if (isProcess) {
      const processCollections: ICollection[] = get(item, 'correlatingData.collections', [])
      processCollectionGroupIds.push(
        ...getValidArray(processCollections).flatMap(collection =>
          getValidArray(collection.groups).map(group => group.id ?? 0)
        )
      )
    } else {
      processCollectionGroupIds.push(
        ...getValidArray((item?.correlatingData as ICollection)?.groups).map(group => group.id ?? 0)
      )
    }

    const isEditorValue =
      !isBasicUser ||
      getValidArray(editorGroupIds).some(
        groupId => getValidArray(item.groupIds).includes(groupId) || processCollectionGroupIds.includes(groupId)
      )

    return {
      id: item?.correlatingId ?? index,
      title: (
        <VStack spacing={1} alignItems="flex-start" width="100%" maxWidth="100%">
          <Highlighter
            className={styles.title}
            highlightClassName={styles.highlight}
            searchWords={[...nameWords, ...searchWords]}
            autoEscape
            caseSensitive
            textToHighlight={`${item?.correlatingId ?? ''} | ${title}`}
            findChunks={findFullWordChunks}
          />
        </VStack>
      ),
      subtitle: (() => {
        if (!searchText || searchText.trim() === '') {
          if (isProcess) {
            const sortedSteps = getValidArray(steps)
              .slice()
              .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            const firstTwoSteps = sortedSteps.slice(0, 2)
            if (firstTwoSteps.length > 0) {
              const stepNames = firstTwoSteps
                .map((step, idx) => {
                  const stepName = step.name ?? ''
                  return stepName ? `${idx + 1}. ${stepName}` : ''
                })
                .filter(Boolean)
              const hasMoreSteps = sortedSteps.length > 2
              if (stepNames.length > 0) {
                return (
                  <VStack spacing={0} alignItems="flex-start">
                    {stepNames.map((stepName, idx) => (
                      <Text key={idx} color={DARK_TEXT_MUTED} fontSize="14px">
                        {stepName}
                        {idx === 1 && hasMoreSteps ? '...' : ''}
                      </Text>
                    ))}
                  </VStack>
                )
              }
            }
            return undefined
          } else {
            const collection = item?.correlatingData as ICollection
            const overview = collection?.overview ?? ''
            if (overview) {
              return (
                <Text color={DARK_TEXT_MUTED} fontSize="14px">
                  {overview}
                </Text>
              )
            }
            return undefined
          }
        }

        return !(searchSourceWithoutEm.includes(title) && title.includes(searchWords.join(' '))) ? (
          <Highlighter
            highlightClassName={styles.highlight}
            searchWords={searchWords}
            autoEscape
            caseSensitive
            findChunks={findFullWordChunks}
            textToHighlight={searchSourceWithoutEm.replace(title, '')}
          />
        ) : undefined
      })(),
      // A Stack carries no badge of its own; only a Collection gets one.
      leadingIcon: isProcess ? undefined : <CollectionIcon />,
      isSelected: isProcess ? selectedProcessIdList.includes(id) : selectedCollectionIdList.includes(id),
      isEditor: isEditorValue,
      originalItem: item,
      onToggleSelect: () => handleSelectItem(item),
      onClick: () => handleItemClick(isProcess, item, index),
      onQuickView: () => {
        isProcess
          ? handleOpenProcessModal(item.correlatingData as IProcessWithRelations)
          : handleOpenCollectionModal(item.correlatingData as ICollection)
      },
      onShare: () => selectAndOpenShareModal(item),
      onArchive: () => {
        clearSelectedItems()
        handleSelectItem(item)
        setIsOpenArchiveModal(true)
      },
      onMakeCopy:
        isProcess && isEditorValue
          ? () => {
              processStore.setSelectedProcess(item.correlatingData as IProcessWithRelations)
              openCopyProcessModal()
            }
          : undefined,
      // Stacks are readable by the whole Organization once published, so sharing one
      // grants nothing and the action is gone. A Collection is still shared to groups.
      canShowShare: !isProcess && !!(item as { isPublished?: boolean })?.isPublished,
      canShowMakeCopy: isProcess && isEditorValue,
      // Archiving a Stack is an authoring action: an Admin may archive any of them,
      // a Coach only the ones they own or collaborate on. A Collection keeps the
      // action it always had.
      canShowArchive: isProcess ? stackCapabilitiesOf(item.correlatingData).canArchive : true
    }
  })

  useEffect(() => {
    if (searchText === 'false') return
    clearSelectedItems()
    // My Stacks and the search results are the same route in two modes. Arriving here
    // without a keyword (sidebar "Stacks", the topbar shortcut, browser back) IS the
    // My Stacks list, so the previous search session's filters have to go with the
    // keyword — otherwise the page keeps showing that narrowed result set under the
    // "My Stacks" title. Only the topbar entry point used to do this reset, which is
    // why it leaked in from every other route into the page.
    if (!searchText) {
      searchStore.setManageMode(false)
      searchStore.clearSearchFilter()
      searchStore.setDisplayTypes([EEsGlobalSearchCollection.PROCESS])
      // Drop the rows too, not just the filters: the table is hidden on an empty
      // result set, so clearing here makes the switch load like a fresh visit
      // (spinner, then the new list) instead of leaving the search hits on screen
      // until the refetch lands. Must run before changeSearchText — that sets
      // isSearching, and clearSearchResults resets it.
      searchStore.clearSearchResults()
    }
    searchStore.changeSearchText(searchText)
  }, [searchText, searchStore, clearSelectedItems])

  useEffect(() => {
    if (page) {
      clearSelectedItems()
      searchStore.changePage(page)
    }
  }, [page, searchStore, clearSelectedItems])

  // Re-scope the search whenever the team switcher changes the active team.
  useEffect(() => {
    if (isFirstTeamRender.current) {
      isFirstTeamRender.current = false
      return
    }
    clearSelectedItems()
    searchStore.search()
  }, [activeOrgId, searchStore, clearSelectedItems])

  useEffect(() => {
    return () => {
      searchStore.clearSearchResults()
      searchStore.setManageMode(false)
      searchStore.clearSearchFilter()
      clearSelectedItems()
    }
  }, [searchStore, clearSelectedItems])

  // Paint the layout scroll container dark for the duration of this page (matches
  // ArchivePage / the other RallyPD dark pages).
  useEffect(() => {
    document.body.setAttribute('data-plans-dark', 'true')
    return () => {
      document.body.removeAttribute('data-plans-dark')
    }
  }, [])

  const selectedKeys: DarkRowKey[] = [
    ...selectedProcessIdList.map(id => `${EEsGlobalSearchCollection.PROCESS}::${id}`),
    ...selectedCollectionIdList.map(id => `${EEsGlobalSearchCollection.COLLECTION}::${id}`)
  ]

  // Share is a Collection action now: a selection that holds a Stack has nothing to
  // share, so the button only appears for a Collection-only selection.
  const canBulkShare = selectedCollectionIdList.length > 0 && selectedProcessIdList.length === 0

  // Archiving in bulk is offered only while every selected Stack is one this
  // viewer may archive — otherwise the button would fail halfway through the list.
  const canBulkArchive = getValidArray(searchResults)
    .filter(
      item =>
        item.collectionName === EEsGlobalSearchCollection.PROCESS &&
        selectedProcessIdList.includes(Number(item.correlatingId))
    )
    .every(item => stackCapabilitiesOf(item.correlatingData).canArchive)

  const manageActionsHeader =
    isManageMode && totalSelectedItems > 0 ? (
      <HStack justify="flex-end" spacing="8px">
        {canBulkShare && (
          <Button
            sx={selectionBarSx}
            isDisabled={!!isSelectedViewOnlyItems}
            leftIcon={<SvgIcon iconName="ic_share" size={16} color={DARK_TEXT_PRIMARY} />}
            onClick={() => setOpenBulkShareModal(true)}
          >
            Share
          </Button>
        )}
        <Button
          sx={selectionBarSx}
          isDisabled={!totalSelectedItems || !!isSelectedViewOnlyItems || !canBulkArchive}
          leftIcon={<SvgIcon iconName="archive" size={16} color={DARK_TEXT_PRIMARY} />}
          onClick={() => setIsOpenArchiveModal(true)}
        >
          Archive
        </Button>
      </HStack>
    ) : null

  const searchResultColumns = buildSearchResultColumns({
    isManageMode,
    canManageActions: !isBasicUser,
    manageActionsHeader
  })

  const hasResults = getValidArray(searchResults).length > 0

  return (
    // spacing=0: Chakra 2.10 Stack spacing renders as flex `gap`, which ADDS to
    // child margins instead of being overridden by them — vertical rhythm is
    // owned by explicit margins on the children (16px, Plans-page standard).
    // overflowX hidden mirrors the Plans/Drafts page-container guard so a child
    // overshooting by a few px can't put a horizontal scrollbar on the viewport.
    // position relative is REQUIRED for that clip to work: closed-but-mounted
    // Chakra Menu poppers (sort dropdown, pagination) are position:absolute and
    // would otherwise resolve their containing block at `.content-page`,
    // bypassing this box and extending the page's scroll width (~2px past the
    // right edge, since this page's rows sit flush at 0 right inset).
    <VStack
      spacing={0}
      padding={0}
      alignItems="stretch"
      width="100%"
      maxWidth="100%"
      overflowX="hidden"
      position="relative"
    >
      {/* Team filter, matching the other coach list pages. `.content-page` already
          pads for the fixed PlayTopbar and the breadcrumb header is in-flow sticky, so
          the banner sits flush at the top — no extra top margin (keeps spacing
          consistent with the other pages). */}
      {/* Full-width like the other list pages (Drafts/Plans have no max-width cap). */}
      {/* Flush like Archive/Drafts — ContextBanner carries its own px/pt, so an
          extra wrapper inset made this page's banner narrower and lower than
          every other list page on a phone. */}
      <ContextBanner />
      <HStack
        width="100%"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={{ base: 2, md: 4 }}
        // 16px banner→toolbar and toolbar→table gaps — Plans page rhythm.
        marginTop="16px"
        // Toolbar keeps a 16px side inset like Plans/Drafts; only the table
        // below is full-bleed.
        paddingX={{ base: '12px', md: '16px' }}
        // Filters only — no Create here. A new Stack is authored as a draft, so the
        // create dialog belongs to the Drafts page; this list shows published ones.
        hidden={!hasResults}
      >
        <Box flex={1} minWidth={0} height={{ base: 10, md: 8 }}>
          <SearchPageHeader dark showTypeTabs={!!searchText} />
        </Box>
      </HStack>

      <VStack marginTop={0} spacing={3} width="full" hidden={getValidArray(searchResults).length === 0}>
        <Box width="full">
          <DarkTable<IItemData>
            columns={searchResultColumns}
            data={dataInTable}
            rowKey={row => `${(row.originalItem as IEsGlobalSearchItem)?.collectionName}::${row.id}`}
            emptyMessage=""
            selectable={isManageMode}
            selectedKeys={selectedKeys}
            onSelectionChange={handleSelectionChange}
            // Row click opens the detail even in manage mode — selection happens
            // only via the checkbox (its cell stops propagation in DarkTable).
            onRowClick={row => row.onClick?.()}
          />
        </Box>
        {getValidArray(searchResults).length > 0 && (
          // 12px side inset — matches the Plans/Drafts pagination wrapper.
          <HStack width="full" justifyContent="center" paddingY={4} paddingX="12px">
            <CkPagination pagination={pagination} pageSize={size} setPageSize={setPageSize} dark />
          </HStack>
        )}
      </VStack>
      {isSearching ? (
        <GlobalSpinner />
      ) : (
        getValidArray(searchResults)?.length === 0 && (
          <VStack width="full" spacing={8} marginTop="24px">
            <EmptyResult dark />
            <UserFeedback dark />
          </VStack>
        )
      )}
      <ProcessSummary isOpen={isOpenProcess} onClose={onCloseProcess} dark />
      <CollectionOverview
        collectionId={selectedCollectionId}
        isCentered
        toggle={isOpenCollection ? onCloseCollection : onOpenCollection}
        isOpen={isOpenCollection}
      />
      {isOpenCopyProcessModal && selectedProcess?.id && (
        <CopyProcessModal
          isOpen={isOpenCopyProcessModal}
          onClose={closeCopyProcessModal}
          processId={selectedProcess?.id}
          dark
        />
      )}

      {isOpenShareCollectionToGroupDialog && (
        <ShareWithGroupsDialog
          refetch={() => {
            searchStore.search()
            setSelectedCollectionIdList([])
          }}
          isOpen={isOpenShareCollectionToGroupDialog}
          toggle={() => {
            if (isOpenShareCollectionToGroupDialog) {
              processStore.setNewProcessGroupIds([])
              processStore.setSelectedProcessId(0)
            }

            setOpenShareCollectionToGroupDialog(!isOpenShareCollectionToGroupDialog)
          }}
          isEdit
          isBasicUser={isBasicUser}
        />
      )}
      {isOpenBulkShareModal && (
        <ControlledShareWithGroupsModal
          isOpen={isOpenBulkShareModal}
          selectedGroupIds={selectedGroupIds}
          setSelectedGroupIds={setSelectedGroupIds}
          toggle={() => setOpenBulkShareModal(!isOpenBulkShareModal)}
          onSubmit={onSubmitBulkShare}
          isBasicUser={isBasicUser}
        />
      )}
      {isOpenArchiveModal && (
        <ModalConfirm
          dark
          title="Archive Items?"
          content="You can find archived items in the Archive page."
          isOpen={isOpenArchiveModal}
          confirmAction="Archive"
          toggle={() => setIsOpenArchiveModal(!isOpenArchiveModal)}
          onAccept={handleArchiveItems}
        />
      )}
      {isOpenConfirmAddToCommonLibrary && (
        <ModalConfirm
          dark
          title="Add all drills to common library?"
          content="You can find all added drills in common library."
          isOpen={isOpenConfirmAddToCommonLibrary}
          confirmAction="Add"
          toggle={onCloseConfirmAddToCommonLibrary}
          onAccept={() =>
            addAllStepsOfProcessToCommonLibrary(selectedProcess?.id, onCloseConfirmAddToCommonLibrary, () => {
              searchStore.search()
            })
          }
        />
      )}
    </VStack>
  )
}

export default observer(SearchPage)
